import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { WishlistApi } from '../api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

export function WishlistProvider({ children }) {
  const { isLoggedIn } = useAuth();
  const [ids, setIds] = useState([]);

  const refresh = useCallback(async () => {
    if (!isLoggedIn) { setIds([]); return; }
    try {
      const { ids: list } = await WishlistApi.ids();
      setIds((list || []).map(String));
    } catch (_) { /* keep what we have */ }
  }, [isLoggedIn]);

  useEffect(() => { refresh(); }, [refresh]);

  const inWishlist = useCallback((productId) => ids.includes(String(productId)), [ids]);

  // Returns false when the visitor needs to log in first.
  const toggle = useCallback(
    async (productId) => {
      if (!isLoggedIn) return false;
      const id = String(productId);
      const has = ids.includes(id);

      // Optimistic — reconcile with the server afterwards.
      setIds((prev) => (has ? prev.filter((x) => x !== id) : [...prev, id]));
      try {
        if (has) await WishlistApi.remove(id);
        else await WishlistApi.add(id);
      } catch (_) {
        refresh();
      }
      return true;
    },
    [ids, isLoggedIn, refresh]
  );

  return (
    <WishlistContext.Provider value={{ ids, count: ids.length, inWishlist, toggle, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
